import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseBlogService } from '@/services/supabaseBlogService';
import { generateSlug } from '@/utils/slugUtils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Upload, Loader2, CheckCircle2, XCircle, Calendar } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, addDays } from 'date-fns';
import { cn } from '@/lib/utils';

interface ParsedPost {
  title: string;
  slug: string;
  metaDescription: string;
  targetKeyword: string;
  content: string;
  categories: string[];
  status: 'draft' | 'scheduled';
  scheduledDate?: Date;
}

interface ImportResult {
  title: string;
  success: boolean;
  error?: string;
}

/**
 * Converts markdown text to basic HTML
 */
function markdownToHtml(md: string): string {
  let html = md
    // Headers
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Links (skip HTML comments)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Process paragraphs (lines that aren't headers, list items, or empty)
  const lines = html.split('\n');
  const processed: string[] = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Skip HTML comments
    if (line.startsWith('<!--') || line.startsWith('-->') || line.includes('<!--')) continue;
    
    // Skip empty lines
    if (!line) {
      if (inList) {
        processed.push('</ul>');
        inList = false;
      }
      continue;
    }

    // Headers already processed
    if (line.startsWith('<h')) {
      if (inList) { processed.push('</ul>'); inList = false; }
      processed.push(line);
      continue;
    }

    // List items
    if (line.startsWith('- ') || line.startsWith('* ')) {
      if (!inList) { processed.push('<ul>'); inList = true; }
      processed.push(`<li>${line.slice(2)}</li>`);
      continue;
    }

    // Regular paragraph
    if (inList) { processed.push('</ul>'); inList = false; }
    if (!line.startsWith('<')) {
      processed.push(`<p>${line}</p>`);
    } else {
      processed.push(line);
    }
  }
  if (inList) processed.push('</ul>');

  return processed.join('\n');
}

/**
 * Parse the structured markdown format into individual posts
 */
function parseBulkContent(raw: string): ParsedPost[] {
  const posts: ParsedPost[] = [];
  
  // Split by "## Blog Post" headers
  const sections = raw.split(/^---\s*$/m).filter(s => s.trim());
  
  for (const section of sections) {
    const titleMatch = section.match(/\*\*Title:\*\*\s*(.+)/);
    const slugMatch = section.match(/\*\*Slug:\*\*\s*\/blog\/(.+)/);
    const keywordMatch = section.match(/\*\*Target keyword:\*\*\s*(.+)/);
    const metaMatch = section.match(/\*\*Meta description:\*\*\s*(.+)/);
    
    if (!titleMatch) continue;
    
    const title = titleMatch[1].trim();
    const slug = slugMatch ? slugMatch[1].trim() : generateSlug(title);
    const targetKeyword = keywordMatch ? keywordMatch[1].trim() : '';
    const metaDescription = metaMatch ? metaMatch[1].trim() : '';
    
    // Extract the actual content (everything after the metadata block)
    // Find the first "---" after metadata, then take content after it
    const contentParts = section.split(/\*\*Meta description:\*\*.+/);
    let contentBody = contentParts.length > 1 ? contentParts[1] : '';
    
    // Remove leading "---" and HTML comments
    contentBody = contentBody.replace(/^[\s\n]*---[\s\n]*/, '');
    contentBody = contentBody.replace(/<!--[\s\S]*?-->/g, '');
    contentBody = contentBody.trim();

    if (!contentBody) continue;

    // Convert markdown to HTML
    const htmlContent = markdownToHtml(contentBody);
    
    // Infer categories from keywords
    const categories: string[] = ['Aging in Place'];
    if (targetKeyword.includes('bathroom') || targetKeyword.includes('kitchen') || targetKeyword.includes('lighting')) {
      categories.push('Home Safety');
    }
    if (targetKeyword.includes('fall') || targetKeyword.includes('balance')) {
      categories.push('Fall Prevention');
    }
    if (targetKeyword.includes('wellness') || targetKeyword.includes('routine')) {
      categories.push('Wellness');
    }
    if (targetKeyword.includes('loneliness') || targetKeyword.includes('connected') || targetKeyword.includes('talk')) {
      categories.push('Family & Connection');
    }
    if (targetKeyword.includes('occupational') || targetKeyword.includes('team')) {
      categories.push('Professional Care');
    }

    posts.push({
      title,
      slug,
      metaDescription,
      targetKeyword,
      content: htmlContent,
      categories,
      status: 'draft',
    });
  }

  return posts;
}

export default function BlogBulkImport() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rawContent, setRawContent] = useState('');
  const [parsedPosts, setParsedPosts] = useState<ParsedPost[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [cadence, setCadence] = useState<'2-per-week' | '3-per-week' | 'daily' | 'manual'>('2-per-week');
  const [startDate, setStartDate] = useState<Date>(addDays(new Date(), 1));

  const handleParse = () => {
    setIsParsing(true);
    try {
      const posts = parseBulkContent(rawContent);
      if (posts.length === 0) {
        toast({ title: 'No posts found', description: 'Could not parse any blog posts from the input. Make sure each post has a **Title:** field.', variant: 'destructive' });
      } else {
        // Auto-assign schedule dates based on cadence
        const scheduled = assignScheduleDates(posts, cadence, startDate);
        setParsedPosts(scheduled);
        toast({ title: `${posts.length} posts parsed`, description: 'Review and adjust before importing.' });
      }
    } catch (e: any) {
      toast({ title: 'Parse error', description: e.message, variant: 'destructive' });
    } finally {
      setIsParsing(false);
    }
  };

  const assignScheduleDates = (posts: ParsedPost[], cadence: string, start: Date): ParsedPost[] => {
    if (cadence === 'manual') return posts;
    
    const daysGap = cadence === 'daily' ? 1 : cadence === '2-per-week' ? 3.5 : 2.33;
    return posts.map((post, i) => ({
      ...post,
      status: 'scheduled' as const,
      scheduledDate: addDays(start, Math.round(i * daysGap)),
    }));
  };

  const handleImport = async () => {
    setIsImporting(true);
    const importResults: ImportResult[] = [];

    for (const post of parsedPosts) {
      try {
        const publishAt = post.scheduledDate ? post.scheduledDate.toISOString() : null;
        
        await supabaseBlogService.createPost({
          title: post.title,
          slug: post.slug,
          content: post.content,
          excerpt: post.metaDescription,
          author_name: 'SeniorThrive Team',
          categories: post.categories,
          tags: [post.targetKeyword, 'aging in place'],
          status: post.status === 'scheduled' ? 'draft' : 'draft',
          seo_meta: { description: post.metaDescription },
          scheduled_publish_at: publishAt,
        });

        importResults.push({ title: post.title, success: true });
      } catch (e: any) {
        importResults.push({ title: post.title, success: false, error: e.message });
      }
    }

    setResults(importResults);
    const successCount = importResults.filter(r => r.success).length;
    toast({
      title: `Import complete`,
      description: `${successCount}/${importResults.length} posts imported successfully.`,
    });
    setIsImporting(false);
  };

  return (
    <div className="container mx-auto py-8 max-w-5xl">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/content')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl font-bold">Bulk Blog Import</h1>
      </div>

      {results.length === 0 && !parsedPosts.length && (
        <Card>
          <CardHeader>
            <CardTitle>Paste Blog Posts</CardTitle>
            <CardDescription>
              Paste your structured blog post content below. Each post should have <code>**Title:**</code>,{' '}
              <code>**Slug:**</code>, <code>**Meta description:**</code>, and content separated by <code>---</code>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={rawContent}
              onChange={(e) => setRawContent(e.target.value)}
              placeholder="Paste your blog posts content here..."
              rows={20}
              className="font-mono text-sm"
            />
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Publishing cadence:</span>
                <Select value={cadence} onValueChange={(v: any) => setCadence(v)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2-per-week">2 per week</SelectItem>
                    <SelectItem value="3-per-week">3 per week</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="manual">Manual (drafts only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cadence !== 'manual' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Starting:</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-[180px] justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                        <Calendar className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={startDate}
                        onSelect={(d) => d && setStartDate(d)}
                        disabled={(d) => d < new Date()}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              <Button onClick={handleParse} disabled={!rawContent.trim() || isParsing}>
                {isParsing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Parse Posts
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {parsedPosts.length > 0 && results.length === 0 && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{parsedPosts.length} Posts Ready to Import</CardTitle>
              <CardDescription>Review the parsed posts below, then click Import All.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {parsedPosts.map((post, i) => (
                  <div key={i} className="flex items-center justify-between border rounded-lg p-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{post.title}</p>
                      <p className="text-sm text-muted-foreground truncate">/blog/{post.slug}</p>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {post.categories.map(c => (
                          <span key={c} className="text-xs bg-muted px-2 py-0.5 rounded">{c}</span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right ml-4 flex-shrink-0">
                      {post.scheduledDate ? (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {format(post.scheduledDate, 'MMM d, yyyy')}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Draft</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => { setParsedPosts([]); }}>
                  Back to Edit
                </Button>
                <Button onClick={handleImport} disabled={isImporting}>
                  {isImporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Import All {parsedPosts.length} Posts
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {results.map((r, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded border">
                  {r.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-destructive flex-shrink-0" />
                  )}
                  <span className="flex-1 truncate">{r.title}</span>
                  {r.error && <span className="text-sm text-destructive">{r.error}</span>}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => { setResults([]); setParsedPosts([]); setRawContent(''); }}>
                Import More
              </Button>
              <Button onClick={() => navigate('/admin/content/blog/list')}>
                View Blog Posts
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
